import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import FlussConditionBuilder from "../FlussConditionBuilder";
import FlussNodePanelShell from "./FlussNodePanelShell";
import FlussNodePanelField from "./FlussNodePanelField";
import { flussNodeTypeLabel } from "./fluss-node-type-label";
import RecordSourceField from "./RecordSourceField";
import { NodeNameField } from "./ActivityNodePanels";

interface GatewayPanelProps {
    selection: FlussElementSelection;
    modeler: any;
    canViewAdvanced: boolean;
    readOnly: boolean;
    onChanged: () => void;
}

const edgePriority = (edge: any) => {
    const config = edge?.extensionElements?.values?.find((value: any) => value.$type === "fluss:Config");
    const parsed = Number(config?.priority);
    return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

// PRIM-PILOT-072 correction (achado #3): businessObject.outgoing is empty on any diagram
// imported via Modeler.importXML() -- confirmed against a real modeler, not just this
// fixture. bpmn-js only ever keeps the outgoing connection list on the diagram-js element
// itself (element.outgoing); each connection's own businessObject (a bpmn:SequenceFlow) is
// what carries id/conditionExpression/extensionElements/etc. Reading businessObject.outgoing
// silently produced zero routes for every real gateway, so the Routes tab never rendered a
// single FlussConditionBuilder outside of tests.
const orderedOutgoing = (element: any) =>
    [...(element?.outgoing || [])]
        .filter((connection: any) => connection?.businessObject?.$type === "bpmn:SequenceFlow")
        .map((connection: any) => connection.businessObject)
        .sort((left, right) => {
            const priority = edgePriority(left) - edgePriority(right);
            return priority || String(left.id).localeCompare(String(right.id));
        });

export const hasIncompleteRouteCondition = (value: unknown): boolean => {
    if (!value) return false;
    let parsed = value;
    if (typeof value === "string") {
        try { parsed = JSON.parse(value); } catch { return false; }
    }
    const node = parsed as any;
    if (node?.type === "condition") return !node.field || !node.operator || (!['empty', 'not_empty'].includes(node.operator) && (node.value === undefined || node.value === ""));
    if (node?.type === "logical" || node?.type === "exists") {
        return Array.isArray(node.conditions) && node.conditions.some(hasIncompleteRouteCondition);
    }
    return false;
};

const routeDestination = (edge: any, t: TFunction) => {
    const target = edge?.targetRef;
    if (!target) return edge?.targetRef?.id || "—";
    if (target.name?.trim()) return target.name.trim();
    const type = String(target.$type || "").replace(/^bpmn:/, "").replace(/([a-z])([A-Z])/g, "$1 $2");
    const translated = flussNodeTypeLabel(target, t) || type;
    return `${translated} · ${target.id}`;
};

const businessRuleCondition = (raw: string): { type: string; recordSource?: string; [key: string]: unknown } | null => {
    try {
        const condition = JSON.parse(raw);
        return condition?.type === "business_rule" ? condition : null;
    } catch { return null; }
};

const eventCatchLabel = (edge: any, t: (key: string) => string) => {
    const target = edge?.targetRef;
    const definitionType = target?.eventDefinitions?.[0]?.$type || "";
    if (target?.$type === "bpmn:IntermediateCatchEvent" && definitionType === "bpmn:SignalEventDefinition") return t("fluss.node_panel.event_gateway.signal_catch");
    if (target?.$type === "bpmn:IntermediateCatchEvent" && definitionType === "bpmn:MessageEventDefinition") return t("fluss.node_panel.event_gateway.message_catch");
    if (target?.$type === "bpmn:IntermediateCatchEvent" && definitionType === "bpmn:TimerEventDefinition") return t("fluss.node_panel.event_gateway.timer_catch");
    return t("fluss.node_panel.event_gateway.unsupported_target");
};

const ensureEdgeConfig = (edge: any, element: any, modeler: any) => {
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    if (!modeling || !moddle) return null;
    let extensionElements = edge.extensionElements;
    if (!extensionElements) {
        extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        modeling.updateProperties(element, { extensionElements });
    }
    let config = (extensionElements.values || []).find((value: any) => value.$type === "fluss:Config");
    if (!config) {
        config = moddle.create("fluss:Config", {});
        config.$parent = extensionElements;
        modeling.updateModdleProperties(element, extensionElements, {
            values: [...(extensionElements.values || []), config],
        });
    }
    return config;
};

// Node-level (not edge-level) fluss:Config — used today only by inclusiveGateway's "exhaustive"
// escape hatch (Fase 9/A3), mirroring ensureEdgeConfig's lazy-create pattern for the node itself
// rather than one of its outgoing edges.
const ensureNodeConfig = (selection: FlussElementSelection, modeler: any) => {
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    if (!modeling || !moddle) return null;
    let extensionElements = selection.businessObject?.extensionElements;
    if (!extensionElements) {
        extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        modeling.updateProperties(selection.element, { extensionElements });
    }
    let config = (extensionElements.values || []).find((value: any) => value.$type === "fluss:Config");
    if (!config) {
        config = moddle.create("fluss:Config", {});
        config.$parent = extensionElements;
        modeling.updateModdleProperties(selection.element, extensionElements, {
            values: [...(extensionElements.values || []), config],
        });
    }
    return config;
};

const isExhaustive = (selection: FlussElementSelection) => {
    const config = (selection.businessObject?.extensionElements?.values || [])
        .find((value: any) => value.$type === "fluss:Config");
    return String(config?.exhaustive || "").toLowerCase() === "true";
};

// Shared by exclusive_gateway (Fase 4) and inclusive_gateway (Fase 9/A3) — same cards-per-route
// UX (UX spec: card per outgoing edge + embedded ConditionBuilder + default badge + drag to
// reorder priority). inclusive additionally offers the "exhaustive" escape hatch when there is no
// default path (Fluss::ValidationIssueCatalog's inclusive_gateway_unverified_exhaustive warning).
const GatewayNodePanel = ({
    kind,
    selection,
    modeler,
    canViewAdvanced,
    readOnly,
    onChanged,
}: GatewayPanelProps & { kind: "exclusive_gateway" | "inclusive_gateway" }) => {
    const { t } = useTranslation();
    const [edges, setEdges] = useState<any[]>(orderedOutgoing(selection.element));
    const [dragIndex, setDragIndex] = useState<number | null>(null);

    useEffect(() => {
        setEdges(orderedOutgoing(selection.element));
    }, [selection.id, selection.businessObject]);

    const registry = modeler?.get("elementRegistry");
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    const defaultEdge = selection.businessObject?.default;
    const conditions = useMemo(
        () => new Map(edges.map((edge) => [edge.id, edge.conditionExpression?.body || ""])),
        [edges]
    );
    const markChanged = () => {
        setEdges(orderedOutgoing(selection.element));
        onChanged();
    };

    const setDefault = (edge: any) => {
        if (!modeling || readOnly) return;
        const edgeElement = registry?.get(edge.id);
        if (edgeElement && edge.conditionExpression) {
            modeling.updateProperties(edgeElement, { conditionExpression: undefined });
        }
        modeling.updateProperties(selection.element, { default: edge });
        markChanged();
    };

    const updateCondition = (edge: any, value: unknown) => {
        if (!modeling || !moddle || readOnly) return;
        const edgeElement = registry?.get(edge.id);
        if (!edgeElement) return;
        const serialized = typeof value === "string" ? value : JSON.stringify(value);
        const expression = serialized.trim()
            ? moddle.create("bpmn:FormalExpression", { body: serialized })
            : undefined;
        modeling.updateProperties(edgeElement, { conditionExpression: expression });
        // PRIM-007/010: this used to call onChanged() only, which doesn't touch this component's
        // own `edges` state. `conditions` is a useMemo keyed on `edges`, so with the same `edges`
        // array reference it kept returning the condition snapshot from mount/last markChanged()
        // call forever -- every field/operator/value pick in the condition builder was written to
        // the model correctly (confirmed by a reload) but immediately appeared to "snap back"
        // blank because FlussConditionBuilder kept being fed that stale `value`. markChanged()
        // (used by every other mutation in this panel) re-reads the edges from the model, which
        // busts the memo.
        markChanged();
    };

    const reorder = (from: number, to: number) => {
        if (from === to || readOnly) return;
        const next = [...edges];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        next.forEach((edge, index) => {
            const element = registry?.get(edge.id);
            const config = ensureEdgeConfig(edge, element, modeler);
            if (config && element) {
                modeling?.updateModdleProperties(element, config, { priority: String(index) });
            }
        });
        setEdges(next);
        onChanged();
    };

    const exhaustive = isExhaustive(selection);
    const setExhaustive = (checked: boolean) => {
        if (readOnly) return;
        const config = ensureNodeConfig(selection, modeler);
        if (!config) return;
        modeling?.updateModdleProperties(selection.element, config, { exhaustive: checked ? "true" : "false" });
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind={kind}
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "routes",
                label: t("fluss.node_panel.tab.routes", { defaultValue: "Routes" }),
                content: (
                    <Stack spacing={1}>
                        <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />
                        {edges.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {t("fluss.node_panel.gateway.empty", {
                                    defaultValue: "Draw an outgoing connection to configure a route.",
                                })}
                            </Typography>
                        )}
                        {edges.map((edge, index) => {
                            const isDefault = defaultEdge === edge;
                            const edgeElement = registry?.get(edge.id);
                            return (
                                <Card
                                    key={edge.id}
                                    variant="outlined"
                                    draggable={!readOnly}
                                    onDragStart={() => setDragIndex(index)}
                                    onDragOver={(event) => event.preventDefault()}
                                    onDrop={() => {
                                        if (dragIndex !== null) reorder(dragIndex, index);
                                        setDragIndex(null);
                                    }}
                                    sx={{
                                        borderColor: isDefault ? "warning.main" : "divider",
                                        borderWidth: isDefault ? 2 : 1,
                                        opacity: dragIndex === index ? 0.65 : 1,
                                    }}
                                    data-testid={`gateway-route-${edge.id}`}
                                >
                                    <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={0.75} alignItems="center">
                                                <DragIndicatorIcon
                                                    fontSize="small"
                                                    color="disabled"
                                                    aria-label={String(t("fluss.node_panel.gateway.drag", {
                                                        defaultValue: "Drag to reorder evaluation priority",
                                                    }))}
                                                />
                                                <TextField
                                                    size="small"
                                                    variant="standard"
                                                    fullWidth
                                                    value={edge.name || ""}
                                                    disabled={readOnly}
                                                    inputProps={{
                                                        style: { fontSize: 13, fontWeight: 500 },
                                                        "aria-label": String(t("fluss.node_panel.gateway.route_name", {
                                                            defaultValue: "Route name",
                                                        })),
                                                    }}
                                                    onChange={(event) => {
                                                        modeling?.updateProperties(edgeElement, {
                                                            name: event.target.value,
                                                        });
                                                        onChanged();
                                                    }}
                                                />
                                                {isDefault && (
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        label={t("fluss.node_panel.gateway.default", {
                                                            defaultValue: "Default path",
                                                        })}
                                                    />
                                                )}
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary">→ {routeDestination(edge, t)}</Typography>
                                            {isDefault ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    {t("fluss.node_panel.gateway.default_help", {
                                                        defaultValue: "The default path has no condition.",
                                                    })}
                                                </Typography>
                                            ) : (
                                                <>
                                                {businessRuleCondition(conditions.get(edge.id) || "") && (
                                                    <RecordSourceField
                                                        value={businessRuleCondition(conditions.get(edge.id) || "")?.recordSource || ""}
                                                        onChange={(next) => updateCondition(edge, {
                                                            ...businessRuleCondition(conditions.get(edge.id) || ""), recordSource: next,
                                                        })}
                                                        disabled={readOnly}
                                                    />
                                                )}
                                                <FlussNodePanelField label={t("fluss.node_panel.start.condition", { defaultValue: "Condition" })} fieldPath="condition" issuePrefix elementId={edge.id}>
                                                <FlussConditionBuilder
                                                    value={conditions.get(edge.id) || ""}
                                                    onChange={(value) => updateCondition(edge, value)}
                                                />
                                                </FlussNodePanelField>
                                                {hasIncompleteRouteCondition(conditions.get(edge.id)) && (
                                                    <Alert severity="warning">
                                                        {t("fluss.node_panel.gateway.incomplete_condition", { defaultValue: "Complete a condição ou ela será ignorada." })}
                                                    </Alert>
                                                )}
                                                </>
                                            )}
                                            {!isDefault && (
                                                <Box>
                                                    <Button
                                                        size="small"
                                                        disabled={readOnly}
                                                        onClick={() => setDefault(edge)}
                                                    >
                                                        {t("fluss.node_panel.gateway.make_default", {
                                                            defaultValue: "Set as default",
                                                        })}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>
                            );
                        })}
                        {kind === "inclusive_gateway" && edges.length >= 2 && !defaultEdge && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={exhaustive}
                                            disabled={readOnly}
                                            onChange={(event) => setExhaustive(event.target.checked)}
                                        />
                                    }
                                    label={t("fluss.node_panel.gateway.exhaustive_toggle", {
                                        defaultValue: "The routes above cover every case (no default path needed)",
                                    })}
                                />
                                <Typography variant="caption" color="text.secondary" display="block">
                                    {t("fluss.node_panel.gateway.exhaustive_help", {
                                        defaultValue: "Publishing is allowed without a default path, but this is not verified automatically.",
                                    })}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                ),
            }]}
        />
    );
};

export const ExclusiveGatewayNodePanel = (props: GatewayPanelProps) => (
    <GatewayNodePanel kind="exclusive_gateway" {...props} />
);

export const InclusiveGatewayNodePanel = (props: GatewayPanelProps) => (
    <GatewayNodePanel kind="inclusive_gateway" {...props} />
);

export const EventBasedGatewayNodePanel = ({
    selection,
    canViewAdvanced,
}: {
    selection: FlussElementSelection;
    canViewAdvanced: boolean;
}) => {
    const { t } = useTranslation();
    const edges = orderedOutgoing(selection.element);

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="event_based_gateway"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "events",
                label: t("fluss.node_panel.tab.event", { defaultValue: "Event" }),
                content: (
                    <Stack spacing={1}>
                        {edges.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                {t("fluss.node_panel.event_gateway.empty", {
                                    defaultValue: "Draw outgoing connections to signal or timer catch events.",
                                })}
                            </Typography>
                        )}
                        {edges.map((edge) => (
                            <Card key={edge.id} variant="outlined" data-testid={`event-gateway-route-${edge.id}`}>
                                <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Typography variant="body2" fontWeight={500}>
                                            {edge.name || edge.id}
                                        </Typography>
                                        <Chip size="small" label={eventCatchLabel(edge, t)} />
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">→ {routeDestination(edge, t)}</Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                ),
            }]}
        />
    );
};

// parallel_gateway (Fase 9/A2) has no configurable fields at all — it unconditionally forks into
// every outgoing path and waits for all of them, so there is nothing to author beyond drawing the
// connections themselves.
export const ParallelGatewayNodePanel = ({
    selection,
    canViewAdvanced,
}: {
    selection: FlussElementSelection;
    canViewAdvanced: boolean;
}) => {
    const { t } = useTranslation();
    const incomingCount = selection.element?.incoming?.length || 0;
    const outgoingCount = selection.element?.outgoing?.length || 0;
    const isJoin = incomingCount > 1 && outgoingCount <= 1;
    return (
        <FlussNodePanelShell
            selection={selection}
            kind="parallel_gateway"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: (
                    <Alert severity="info">
                        {isJoin
                            ? t("fluss.node_panel.parallel.join_info", { defaultValue: "Este gateway espera todos os caminhos chegarem antes de continuar." })
                            : t("fluss.node_panel.parallel.info", { defaultValue: "Este gateway inicia todos os caminhos de saída ao mesmo tempo." })}
                    </Alert>
                ),
            }]}
        />
    );
};

export default ExclusiveGatewayNodePanel;
